import { useEffect, useState } from "react";
import axios from "axios";
import "../../.csscode/PopUpModal.css";
import "../../.csscode/TablePage.css";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CustomLoader from "../static/CustomLoader";

const NAVY = "#123B5D";
const PAGE_BG = "#F4F7FB";
const CARD_BORDER = "#DCE4EC";
const TEXT_MUTED = "#6B7A89";

const STATUS = {
  open: "#F5A524",
  closed: "#2E9E6B",
  progress: "#2F7DD1",
};

const cardSx = {
  height: "100%",
  borderRadius: "12px",
  border: `1px solid ${CARD_BORDER}`,
  boxShadow: "none",
  backgroundColor: "#FFFFFF",
};

const tooltipStyle = {
  borderRadius: 8,
  border: `1px solid ${CARD_BORDER}`,
  boxShadow: "0 6px 18px rgba(15,35,55,0.12)",
  fontSize: 12.5,
};

const SummaryBox = ({ title, value, icon, color }) => (
  <Box
    sx={{
      height: "100%",
      px: 1.75,
      py: 1.5,
      borderRadius: "10px",
      backgroundColor: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.08)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        color: "#A9C1D6",
        "& svg": { fontSize: 17, color },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 12.5, color: "#A9C1D6", lineHeight: 1.2 }}>
        {title}
      </Typography>
    </Box>
    <Typography
      sx={{
        mt: 0.75,
        fontSize: 26,
        fontWeight: 600,
        lineHeight: 1.15,
        color: "#FFFFFF",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ChartHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: 15, fontWeight: 600, color: NAVY }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED }}>
      {subtitle}
    </Typography>
  </Box>
);

const WorkListDashboard = ({}) => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");
  const [totalOpenWI, setTotalOpenWI] = useState("");
  const [totalCloseWI, setTotalCloseWI] = useState("");
  const [totalWorkingWI, setTotalWorkingWI] = useState("");
  const [totaldeptActive, setTotaldeptActive] = useState("");
  const [totalEmpActive, setTotalEmpActive] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, SetUser] = useState("");

  useEffect(() => {
    fetchWorkItemCount();
  }, []);

  const resetWorkList = async () => {
    setTotalOpenWI("");
    setTotalCloseWI("");
    setTotalWorkingWI("");
    setTotaldeptActive("");
    setTotalEmpActive("");
  };
  const fetchWorkItemCount = async () => {
    const savedUser = localStorage.getItem("user");
    if (!savedUser) {
      console.error("No user found in local storage");
      return;
    }

    const parsedUser = JSON.parse(savedUser);
    SetUser(parsedUser.userid);

    // Ensure parsedUser has the necessary property
    if (!parsedUser.empcode) {
      console.error("Parsed user does not have 'empcode'");
      return; // Exit if empcode is missing
    }

    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${dbTokenData}`,
        "Content-Type": "application/json",
        dbMaster: "",
        userId: parsedUser.empcode,
      };
      axios.get(`${apiUrl}worklistdashboard`, { headers }).then((response) => {
        if (response.status === 200) {
          setTotalOpenWI(response.data.totalOpen);
          setTotalCloseWI(response.data.totalClose);
          setTotalWorkingWI(response.data.totalWorking);
          setTotaldeptActive(response.data.totalDepartment);
          setTotalEmpActive(response.data.totalEmployee);
          setLoading(false);
        } else {
          toast.info(response.data.description);
          setLoading(false);
        }
      });
    } catch (error) {
      setLoading(false);
      toast.error(error);
      resetWorkList();
    }
  };

  const summaryData = [
    {
      title: "Total Open",
      value: totalOpenWI || 0,
      icon: <FolderOpenOutlinedIcon />,
      color: "#F5C25A",
    },
    {
      title: "Total Closed",
      value: totalCloseWI || 0,
      icon: <CheckCircleOutlineIcon />,
      color: "#6FD0A0",
    },
    {
      title: "Work in Progress",
      value: totalWorkingWI || 0,
      icon: <AutorenewIcon />,
      color: "#7DB6F0",
    },
    {
      title: "Departments",
      value: totaldeptActive || 0,
      icon: <ApartmentOutlinedIcon />,
      color: "#B7A3EE",
    },
    {
      title: "Employees",
      value: totalEmpActive || 0,
      icon: <PeopleAltOutlinedIcon />,
      color: "#F2A08C",
    },
  ];

  const chartData = [
    { name: "Open", value: totalOpenWI || 0 },
    { name: "Closed", value: totalCloseWI || 0 },
    { name: "In Progress", value: totalWorkingWI || 0 },
  ];

  const COLORS = [STATUS.open, STATUS.closed, STATUS.progress];

  // display-only: percentage shown in the legend next to the pie
  const chartTotal = chartData.reduce((sum, d) => sum + Number(d.value), 0);
  const percentOf = (v) =>
    chartTotal ? Math.round((Number(v) / chartTotal) * 100) : 0;

  const barChartData = [
    { status: "Open Work Items", count: totalOpenWI || 0 },
    { status: "Closed Work Items", count: totalCloseWI || 0 },
    { status: "Work in Progress", count: totalWorkingWI || 0 },
  ];

  // Function to determine the color based on the bar name
  const getBarColor = (name) => {
    switch (name) {
      case "Open Work Items":
        return STATUS.open;
      case "Closed Work Items":
        return STATUS.closed;
      case "Work in Progress":
        return STATUS.progress;
      case "Total Work in Progress":
        return "#9AA7B4";
      case "Total Delayed Work Items":
        return "#E5484D";
      default:
        return "#8884d8";
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        backgroundColor: PAGE_BG,
        borderRadius: 2,
      }}
    >
      {/* Welcome banner with KPIs */}
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "14px",
          backgroundColor: NAVY,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "1.25rem", md: "1.5rem" },
            fontWeight: 600,
            lineHeight: 1.25,
            color: "#FFFFFF",
          }}
        >
          Welcome back, {user || "Admin"}
        </Typography>
        <Typography
          sx={{ mt: 0.5, mb: 2.25, fontSize: 13.5, color: "#A9C1D6" }}
        >
          Here’s a quick overview of your application performance.
        </Typography>

        <Grid container spacing={1.5}>
          {summaryData.map((item, index) => (
            <Grid item xs={6} sm={4} md={2.4} key={index}>
              <SummaryBox
                title={item.title}
                value={item.value}
                icon={item.icon}
                color={item.color}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <ChartHeader
                title="Application Distribution"
                subtitle="Breakdown of application status submitted."
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box sx={{ width: 220, height: 220, mx: "auto" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={95}
                        dataKey="value"
                        stroke="#FFFFFF"
                        strokeWidth={2}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ flex: 1, minWidth: 160, mx: "auto" }}>
                  {chartData.map((d, index) => (
                    <Box
                      key={d.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        py: 1,
                        borderBottom:
                          index < chartData.length - 1
                            ? "1px solid #EEF2F6"
                            : "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <Typography sx={{ fontSize: 13.5, color: "#3B4A59" }}>
                        {d.name}
                      </Typography>
                      <Typography
                        sx={{
                          ml: "auto",
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: NAVY,
                        }}
                      >
                        {d.value}
                        <Box
                          component="span"
                          sx={{
                            ml: 0.75,
                            fontSize: 12,
                            fontWeight: 400,
                            color: TEXT_MUTED,
                          }}
                        >
                          {percentOf(d.value)}%
                        </Box>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <ChartHeader
                title="Monthly Application Status Overview"
                subtitle="Visualize trends across different work statuses over time."
              />
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={barChartData}
                  margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E3E9EF"
                  />
                  <XAxis
                    dataKey="status"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12.5, fill: TEXT_MUTED }}
                    tickFormatter={(v) => v.replace(" Work Items", "")}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: TEXT_MUTED }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(18,59,93,0.05)" }}
                    contentStyle={tooltipStyle}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={72}>
                    {barChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getBarColor(entry.status)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && <CustomLoader />}
    </Box>
  );
};

export default WorkListDashboard;
